/** Keycloak / any-compliant-OIDC implementation (react-oidc-context, code +
 *  PKCE, discovery-based). Selected by NEXT_PUBLIC_AUTH_PROVIDER=keycloak —
 *  set ONLY in the self-contained k3s deployment (sub-project C), never in
 *  DigitalOcean. Client-only: oidc-client-ts needs window/sessionStorage, so
 *  the provider mounts after hydration (the app is fully auth-gated anyway —
 *  there is no meaningful SSR content behind the login wall). */
import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Router from "next/router";
import {
  AuthProvider as OidcProvider,
  hasAuthParams,
  useAuth as useOidc,
} from "react-oidc-context";
import type { User } from "oidc-client-ts";

import { mapUser, normalizeReturnTo, oidcSigninArgs } from "../mapping";
import type { AuthHook, GetTokenOptions, LoginOptions } from "../types";

const onSigninCallback = (user: User | void) => {
  const state = (user && (user.state as { returnTo?: string } | undefined)) || undefined;
  Router.replace(normalizeReturnTo(state?.returnTo));
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // oidc-client-ts requires browser storage; render nothing until mounted.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const issuer = process.env.NEXT_PUBLIC_OIDC_ISSUER;
  const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
  const redirectUri =
    process.env.NEXT_PUBLIC_APP_BASE_URL ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    process.env.APP_BASE_URL;
  if (!issuer || !clientId || !redirectUri) {
    throw new Error(
      "OIDC provider is not configured. Set NEXT_PUBLIC_OIDC_ISSUER, NEXT_PUBLIC_OIDC_CLIENT_ID, and NEXT_PUBLIC_APP_BASE_URL.",
    );
  }
  if (!mounted) return null;
  return (
    <OidcProvider
      authority={issuer}
      client_id={clientId}
      redirect_uri={redirectUri}
      post_logout_redirect_uri={redirectUri}
      scope="openid profile email"
      automaticSilentRenew
      onSigninCallback={onSigninCallback}
    >
      {children}
    </OidcProvider>
  );
}

export function useAuth(): AuthHook {
  const oidc = useOidc();

  const user = useMemo(() => mapUser(oidc.user?.profile), [oidc.user]);

  const login = useCallback(
    async (opts?: LoginOptions) => {
      await oidc.signinRedirect(oidcSigninArgs(opts));
    },
    [oidc.signinRedirect],
  );

  const logout = useCallback(() => {
    void oidc.signoutRedirect();
  }, [oidc.signoutRedirect]);

  const getAccessToken = useCallback(
    async (opts?: GetTokenOptions): Promise<string> => {
      if (opts?.forceRefresh) {
        const refreshed = await oidc.signinSilent();
        if (refreshed?.access_token) return refreshed.access_token;
        throw new Error("Silent token refresh failed.");
      }
      const token = oidc.user?.access_token;
      if (!token) throw new Error("No access token available.");
      return token;
    },
    [oidc.signinSilent, oidc.user],
  );

  return {
    isLoading: oidc.isLoading,
    isAuthenticated: oidc.isAuthenticated,
    // A background silent-renew failure sets oidc.error while the user is
    // still authenticated; surfacing it would let DefaultLayout replace the
    // running app with a full-screen error. Only surface errors that actually
    // block auth (parity with @auth0/auth0-react, which never reports
    // background-renew failures here).
    error: oidc.isAuthenticated ? undefined : (oidc.error as Error | undefined),
    user,
    login,
    logout,
    getAccessToken,
  };
}

/** Page guard with withAuthenticationRequired semantics: wait while loading,
 *  bounce to signin (preserving the path) when unauthenticated. */
export const withAuthRequired = <P extends object>(
  Page: ComponentType<P>,
): ComponentType<P> => {
  const Guarded = (props: P) => {
    const oidc = useOidc();
    useEffect(() => {
      if (
        !oidc.isLoading &&
        !oidc.isAuthenticated &&
        !oidc.activeNavigator &&
        !hasAuthParams()
      ) {
        void oidc.signinRedirect(
          oidcSigninArgs({
            returnTo: window.location.pathname + window.location.search,
          }),
        );
      }
    }, [oidc.isLoading, oidc.isAuthenticated, oidc.activeNavigator]);
    return oidc.isAuthenticated ? <Page {...props} /> : null;
  };
  Guarded.displayName = `withAuthRequired(${Page.displayName || Page.name || "Page"})`;
  return Guarded;
};
